import { useNavigate } from "react-router-dom";
import actions from "store/actions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { companyMetadataSelector, userMetadataSelector } from "store/selectors";

import {
  getCompany,
  getSessionId,
  getUser,
  googleAuth,
  login,
  loginWithPassword,
  logout as logoutUser,
  selectActiveCompany,
  sendMagicLink,
} from "services/auth";
import { clearSalsaData, saveSalsaData } from "services/salsa";
import { IUserWithCompany } from "types/user";

import { GetCompanyByIdResponseDto, UserProfileResponseDto } from "utils/swagger_react_query";

type TGetUserAndCompanyMethod = (
  useCache?: boolean,
  withNavigate?: boolean,
  shouldSaveToStore?: boolean,
) => Promise<IUserWithCompany | undefined>;

type TGetUserMethod = (
  useCache?: boolean,
  withNavigate?: boolean,
  shouldSaveToStore?: boolean,
) => Promise<UserProfileResponseDto | undefined | null>;

const useAuth = () => {
  const currentUser = useAppSelector(userMetadataSelector);
  const currentCompany = useAppSelector(companyMetadataSelector);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // NOTE::: STORE ACTIONS
  const saveUserToStore = async (user?: UserProfileResponseDto) => {
    dispatch(actions.userMetaData.setUserMetaData(user || null));
  };

  const saveCompanyToStore = async (company?: GetCompanyByIdResponseDto) => {
    dispatch(actions.userMetaData.setCompanyData(company || null));
  };

  const saveUserAndCompanyToStore = async (
    user?: UserProfileResponseDto,
    company?: GetCompanyByIdResponseDto | null,
  ) => {
    if (user) saveUserToStore(user);
    if (company) saveCompanyToStore(company);
  };

  // NOTE::: DATA SELECTORS
  const isAuthenticated = () => !!getSessionId();

  const getCompanyInfo = async (companyId: string, shouldSaveToStore: boolean = false) => {
    const fetchCompanyRes = await getCompany(companyId);
    if (shouldSaveToStore) {
      dispatch(actions.userMetaData.setCompanyData(fetchCompanyRes));
    }
    return fetchCompanyRes;
  };

  const getCurrentUser: TGetUserMethod = async (
    useCache: boolean = false,
    withNavigate: boolean = false,
    shouldSaveToStore: boolean = true,
  ) => {
    if (!isAuthenticated()) return;

    if (withNavigate) {
      await logout(false, withNavigate);
      return;
    }
    if (useCache && currentUser) return currentUser;

    const userResponse = await getUser();
    if (shouldSaveToStore) saveUserToStore(userResponse);

    return userResponse;
  };

  const getCurrentUserAndCompany: TGetUserAndCompanyMethod = async (
    useCache: boolean = false,
    withNavigate: boolean = false,
    shouldSaveToStore: boolean = true,
  ) => {
    if (!isAuthenticated()) return;

    if (withNavigate) {
      await logout(false, withNavigate);
      return;
    }
    if (useCache && currentUser && currentCompany)
      return {
        user: currentUser,
        company: currentCompany,
      };

    const userResult = await getUser();
    const companyResult = await getCompanyInfo(userResult?.activeCompanyUser?.companyId || "");
    if (shouldSaveToStore) saveUserAndCompanyToStore(userResult, companyResult);

    return {
      user: userResult,
      company: companyResult,
    };
  };

  // NOTE::: AUTH ACTIONS

  const logout = async (
    withRequest: boolean = true,
    withNavigate: boolean = true,
    params?: string,
  ) => {
    await logoutUser(withRequest, withNavigate, params);
    dispatch(actions.userMetaData.setUserMetaData(null));
    dispatch(actions.userMetaData.setCompanyData(null));
    dispatch(actions.salsaData.setSalsa(null));
    dispatch(actions.subscriptionPricesData.setSubscriptionPrices(null));
    dispatch(actions.userMetaData.setCompaniesList(null));
    dispatch(actions.userMetaData.setUserRewardPoints(null));
  };

  const selectActiveCompanyAndClearSalsaStoreData = async (companyId: string) => {
    dispatch(actions.salsaData.setSalsa(null));
    clearSalsaData();
    const tokenRes = await selectActiveCompany(companyId);
    saveSalsaData(tokenRes);
  };

  return {
    //NOTE::: store actions
    saveUserAndCompanyToStore,
    saveUserToStore,
    saveCompanyToStore,
    // NOTE::: data selectors
    isAuthenticated,
    getCompanyInfo,
    getCurrentUser,
    getCurrentUserAndCompany,
    // NOTE::: auth actions
    selectActiveCompany: selectActiveCompanyAndClearSalsaStoreData,
    sendMagicLink,
    googleAuth,
    login,
    loginWithPassword,
    logout,
  };
};

export default useAuth;
